import { useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { useStatisticsTracking } from '@/hooks/trackingTime';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { getWorkingDaysInMonth } from '@/utils/time';

const StatisticsTracking = () => {
    const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
    const { data, isLoading } = useStatisticsTracking(selectedMonth);
    const navigate = useNavigate();

    // Memoize the formatted time function to prevent recreation on every render
    const formatTime = useCallback((minutes: number) => {
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;

        // Format 1: "1h 15m"
        const format1 = `${hours}h ${remainingMinutes}m`;

        // Format 2: "75 minutes"
        const format2 = `${minutes} minutes`;

        // Tính ra số giờ thực: ví dụ 75 phút → 1.25 giờ
        const decimalHours = minutes / 60;

        // === Cách B: LÀM TRÒN GẦN NHẤT (nearest 0.25) ===
        const roundedHours = Math.round(decimalHours * 4) / 4;

        // Hiển thị 2 chữ số thập phân, vd: "1.25 hours"
        const format3 = `${roundedHours.toFixed(2)} hours`;

        return (
            <div className="space-y-1">
                <div>{format1}</div>
                <div className="text-sm text-muted-foreground">{format2}</div>
                <div className="text-sm text-muted-foreground">{format3}</div>
            </div>
        );
    }, []);

    const handleMonthSelect = useCallback((date: Date | undefined) => {
        if (date) {
            const firstDayOfMonth = new Date(
                date.getFullYear(),
                date.getMonth(),
                1
            );
            setSelectedMonth(firstDayOfMonth);
        }
    }, []);

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const [year, month] = e.target.value.split('-').map(Number);
            if (year && month) {
                handleMonthSelect(new Date(year, month - 1, 1));
            }
        },
        [handleMonthSelect]
    );

    // Memoize the month value to prevent unnecessary re-renders
    const monthValue = useMemo(
        () => dayjs(selectedMonth).format('YYYY-MM'),
        [selectedMonth]
    );

    // Memoize summary statistics
    const summaryStats = useMemo(() => {
        if (!data || data.length === 0) return null;

        return {
            totalUsers: data.length,
            totalWorkingTime: data.reduce((sum, row) => sum + row.totalTime, 0),
            totalReports: data.reduce((sum, row) => sum + row.reports, 0),
            totalCheckIns: data.reduce((sum, row) => sum + row.checkIns, 0),
            totalWfh: data.reduce((sum, row) => sum + row.wfh, 0),
            totalLateDays: data.reduce((sum, row) => sum + row.lateDays, 0),
            totalLeave: data.reduce((sum, row) => sum + row.totalLeave, 0),
        };
    }, [data]);

    const totalWorkingDays = useMemo(() => {
        return getWorkingDaysInMonth(
            selectedMonth.getFullYear(),
            selectedMonth.getMonth() + 1
        );
    }, [selectedMonth]);

    const getTotalDayNotReport = ({
        totalReport,
        totalCheckIn,
    }: {
        totalReport: number;
        totalCheckIn: number;
        totalLeave: number;
    }) => {
        let totalNotReport = 0;
        if (totalCheckIn > totalWorkingDays) {
            totalNotReport = totalWorkingDays - totalReport;
        }

        totalNotReport = totalCheckIn - totalReport;

        return totalNotReport < 0 ? 0 : totalNotReport;
    };

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(-1)}
                        >
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                        <CardTitle>Working Time Statistics</CardTitle>
                    </div>
                    <div className="flex items-center space-x-2">
                        <Input
                            type="month"
                            value={monthValue}
                            onChange={handleInputChange}
                            className="w-full sm:w-[200px]"
                        />
                        {isLoading && (
                            <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
                        )}
                    </div>
                </div>

                {/* Summary Statistics */}
                {summaryStats && (
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-4 p-4 bg-muted/50 rounded-lg">
                        <div className="text-center">
                            <p className="text-sm text-muted-foreground">
                                Total Users
                            </p>
                            <p className="text-lg font-semibold">
                                {summaryStats.totalUsers}
                            </p>
                        </div>
                        <div className="text-center">
                            <p className="text-sm text-muted-foreground">
                                Total Hours
                            </p>
                            <p className="text-lg font-semibold">
                                {Math.round(summaryStats.totalWorkingTime / 60)}
                                h
                            </p>
                        </div>
                        {/* <div className="text-center">
                            <p className="text-sm text-muted-foreground">
                                Reports
                            </p>
                            <p className="text-lg font-semibold">
                                {summaryStats.totalReports}
                            </p>
                        </div> */}
                        <div className="text-center">
                            <p className="text-sm text-muted-foreground">
                                Check-ins
                            </p>
                            <p className="text-lg font-semibold">
                                {summaryStats.totalCheckIns}
                            </p>
                        </div>
                        <div className="text-center">
                            <p className="text-sm text-muted-foreground">WFH</p>
                            <p className="text-lg font-semibold">
                                {summaryStats.totalWfh}
                            </p>
                        </div>
                        <div className="text-center">
                            <p className="text-sm text-muted-foreground">
                                Late Days
                            </p>
                            <p className="text-lg font-semibold">
                                {summaryStats.totalLateDays}
                            </p>
                        </div>
                        <div className="text-center">
                            <p className="text-sm text-muted-foreground">
                                Total Leave
                            </p>
                            <p className="text-lg font-semibold">
                                {summaryStats.totalLeave}
                            </p>
                        </div>
                    </div>
                )}
            </CardHeader>
            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>User Name</TableHead>
                            <TableHead>Total Working Time</TableHead>
                            {/* <TableHead>Number of Reports</TableHead> */}
                            <TableHead>Number of No Reports</TableHead>
                            <TableHead>Number of Check-ins</TableHead>
                            <TableHead>Number of WFH</TableHead>
                            <TableHead>Number of Late Days</TableHead>
                            <TableHead>Number of Leave Days</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={6}
                                    className="text-center py-8"
                                >
                                    <div className="flex items-center justify-center space-x-2">
                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                        <span>Loading data...</span>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : data.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={6}
                                    className="text-center py-8"
                                >
                                    <p className="text-muted-foreground">
                                        No data for selected month
                                    </p>
                                </TableCell>
                            </TableRow>
                        ) : (
                            data.map((row) => (
                                <TableRow key={row.key}>
                                    <TableCell className="font-medium">
                                        {row.name}
                                    </TableCell>
                                    <TableCell>
                                        {formatTime(row.totalTime)}
                                    </TableCell>
                                    {/* <TableCell>{row.reports}</TableCell> */}
                                    <TableCell>
                                        {getTotalDayNotReport({
                                            totalReport: row.reports,
                                            totalCheckIn: row.checkIns,
                                            totalLeave: row.totalLeave,
                                        })}
                                    </TableCell>
                                    <TableCell>{row.checkIns}</TableCell>
                                    <TableCell>{row.wfh}</TableCell>
                                    <TableCell>{row.lateDays}</TableCell>
                                    <TableCell>{row.totalLeave}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
};

export default StatisticsTracking;
